import React, {useCallback, useEffect, useRef, useState} from "react";
import {Form, Table} from "react-bootstrap";
import {useNavigate} from "react-router";
import {format, startOfMonth} from "date-fns";
import AdminApi from "common/api";
import Pagination from "common/components/Pagination";
import CustomDatePicker from "common/components/DatePicker";
import {dateFormat} from "common/utils";

const PAGE_SIZE = 20;

function toStartDateParam(date) {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd 00:00:00");
}

function toEndDateParam(date) {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd 23:59:59");
}

export default function InviteRanking() {
  const api = new AdminApi();
  const navigate = useNavigate();
  const currentPage = useRef(1);
  const now = new Date();

  const [startDate, setStartDate] = useState(startOfMonth(now));
  const [endDate, setEndDate] = useState(now);
  const [minCount, setMinCount] = useState("1");
  const [keyword, setKeyword] = useState("");

  const [appliedStart, setAppliedStart] = useState(startOfMonth(now));
  const [appliedEnd, setAppliedEnd] = useState(now);
  const [appliedMinCount, setAppliedMinCount] = useState(1);
  const [appliedKeyword, setAppliedKeyword] = useState("");

  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searched, setSearched] = useState(false);

  const fetchRanking = useCallback((page = 1) => {
    if (!appliedStart || !appliedEnd) return;
    api.getAdminInviteRanking({
      startDate: toStartDateParam(appliedStart),
      endDate: toEndDateParam(appliedEnd),
      minCount: appliedMinCount,
      keyword: appliedKeyword,
      page,
      countPerPage: PAGE_SIZE,
    }).then((res) => {
      setSearched(true);
      if (res?.result === "ok") {
        setUsers(res.users ?? []);
        setTotalCount(res.pageInfo?.totalItemCount ?? 0);
        currentPage.current = page;
      } else {
        setUsers([]);
        setTotalCount(0);
      }
    });
  }, [appliedStart, appliedEnd, appliedMinCount, appliedKeyword]);

  useEffect(() => {
    fetchRanking(1);
  }, [fetchRanking]);

  function onSearch(e) {
    e.preventDefault();
    const parsedMin = Number(minCount);
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
    setAppliedMinCount(Number.isInteger(parsedMin) && parsedMin > 0 ? parsedMin : 1);
    setAppliedKeyword(keyword.trim());
  }

  function onReset() {
    const nextStart = startOfMonth(new Date());
    const nextEnd = new Date();
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setMinCount("1");
    setKeyword("");
    setAppliedStart(nextStart);
    setAppliedEnd(nextEnd);
    setAppliedMinCount(1);
    setAppliedKeyword("");
  }

  return (
    <div className="user-admin">
      <div className="tab-depth-line">
        <h1 onClick={() => navigate("/user")} style={{cursor: "pointer"}}>사용자 관리</h1>
        <h1>&gt; 초대 이벤트 집계</h1>
      </div>

      <p className="user-invite-ranking-note">
        선택한 기간에 가입한 초대 친구 수 기준으로 집계합니다. 누적 초대와는 다를 수 있습니다.
      </p>

      <form className="user-invite-ranking-filter flex" onSubmit={onSearch}>
        <CustomDatePicker
          placeholder="시작일"
          value={startDate}
          onChange={setStartDate}
          maxDate={endDate ?? undefined}
        />
        <span>~</span>
        <CustomDatePicker
          placeholder="종료일"
          value={endDate}
          onChange={setEndDate}
          minDate={startDate ?? undefined}
        />
        <Form.Control
          type="number"
          min={1}
          placeholder="최소 초대수"
          value={minCount}
          onChange={(e) => setMinCount(e.target.value)}
          style={{maxWidth: 140}}
        />
        <Form.Control
          type="text"
          placeholder="닉네임 또는 회원번호"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{maxWidth: 220}}
        />
        <button type="submit" className="blue-btn">검색</button>
        <button type="button" className="grey-btn" onClick={onReset}>초기화</button>
      </form>

      <Table bordered className="basic-table user-list-table" style={{marginTop: 24}}>
        <thead>
          <tr>
            <th>순위</th>
            <th>회원번호</th>
            <th>닉네임</th>
            <th>기간 초대수</th>
            <th>누적 초대</th>
            <th>포인트</th>
            <th>상태</th>
            <th>가입일</th>
          </tr>
        </thead>
        <tbody>
          {users.length > 0 ? users.map((user, idx) => (
            <tr key={user.userNo}>
              <td>{(currentPage.current - 1) * PAGE_SIZE + idx + 1}</td>
              <td>{user.userNo}</td>
              <td>
                <button
                  type="button"
                  className="user-link-btn"
                  onClick={() => window.open(`/user/${user.userNo}`, "_blank", "noopener,noreferrer")}
                >
                  {user.nickname || "-"}
                </button>
              </td>
              <td>{user.periodInviteCount ?? 0}</td>
              <td>{user.inviteBuddyCount ?? 0}</td>
              <td>{user.point?.toLocaleString() ?? 0}</td>
              <td>{user.status || "-"}</td>
              <td>{dateFormat(user.createdAt, true)}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={8} style={{textAlign: "center"}}>
                {searched ? "조건에 맞는 사용자가 없습니다." : "기간을 선택하고 검색해주세요."}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {totalCount > 0 &&
        <Pagination
          total={totalCount}
          pageSize={PAGE_SIZE}
          page={currentPage.current}
          onPageChange={fetchRanking}
        />
      }
    </div>
  );
}
